/**
 * Sender-side file-transfer resume state — CRUD over `ReflectoStorage.ftSenderStates`
 * (docs/file-transfer-resume.md §3.1.2 — "same keys, same shapes" as the
 * extension). Web's `webStorage` (IndexedDB) mirror of
 * `apps/extension/src/filetransfer/senderState.ts` — identical field
 * semantics and single-writer promise-queue discipline, swapped onto
 * `webStorage.get`/`webStorage.set`.
 *
 * This module is a PLAIN typed key-value CRUD layer only — it does NOT apply
 * the §2.4 load-time validation or any OPFS artifact cleanup; that logic
 * lives in `senderResumeDeps.web.ts` (mirrors the extension's split).
 *
 * SECURITY: `manifestJson` (and by extension any record) is never logged —
 * security.md.
 */

import {
  FT_STATE_MAX_RECORDS,
  FT_STATE_RETENTION_MS,
  type FtSenderStateRecord,
  isTerminalSenderStatus,
} from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";

/** Single in-flight read-modify-write cycle for this module (see module doc). */
let writeQueue: Promise<void> = Promise.resolve();

function enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
  const result = writeQueue.then(fn, fn);
  writeQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

async function readMap(): Promise<Record<string, FtSenderStateRecord>> {
  const { ftSenderStates } = await webStorage.get("ftSenderStates");
  return ftSenderStates ?? {};
}

/** Persist `record` (full replace, keyed by `record.transferId`). P-S1/P-S2/P-S3/P-S4 all call through this. */
export function saveSenderState(record: FtSenderStateRecord): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    map[record.transferId] = record;
    await webStorage.set({ ftSenderStates: map });
  });
}

/** Plain read — no §2.4 validation (see module doc). */
export async function getSenderState(transferId: string): Promise<FtSenderStateRecord | undefined> {
  const map = await readMap();
  return map[transferId];
}

/** Plain read of every record — no §2.4 validation (see module doc). */
export async function listSenderStates(): Promise<FtSenderStateRecord[]> {
  const map = await readMap();
  return Object.values(map);
}

/** Delete one record. No-op if `transferId` is unknown. */
export function removeSenderState(transferId: string): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    if (!(transferId in map)) return;
    delete map[transferId];
    await webStorage.set({ ftSenderStates: map });
  });
}

/**
 * Lazy tombstone prune (§8.5): drop terminal records older than
 * `FT_STATE_RETENTION_MS` (7 days), then — if more than
 * `FT_STATE_MAX_RECORDS` (50) terminal records remain — drop the oldest
 * terminal ones beyond the cap. Active records are NEVER pruned.
 */
export function pruneSenderStates(now: number = Date.now()): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    const cutoff = now - FT_STATE_RETENTION_MS;

    for (const [id, rec] of Object.entries(map)) {
      if (isTerminalSenderStatus(rec.status) && rec.updatedAt < cutoff) {
        delete map[id];
      }
    }

    const terminalIds = Object.entries(map)
      .filter(([, rec]) => isTerminalSenderStatus(rec.status))
      .sort(([, a], [, b]) => a.updatedAt - b.updatedAt)
      .map(([id]) => id);

    const overflow = terminalIds.length - FT_STATE_MAX_RECORDS;
    if (overflow > 0) {
      for (const id of terminalIds.slice(0, overflow)) {
        delete map[id];
      }
    }

    await webStorage.set({ ftSenderStates: map });
  });
}
